import { ClassificationSource, WorkItem, WorkItemSourceLineage } from '../api/workItems';

/**
 * Garbage titles that should be treated as if the field were empty.
 * Matched case-insensitively after trimming.
 */
const GARBAGE_TITLES = new Set([
  'new',
  'untitled',
  'unknown',
  'no title',
  '(no subject)',
  '',
]);
// Keep these placeholder sets aligned with backend/services/application_service.py.
// Story 2.3 intentionally mirrors the display fallback chain across frontend/backend.
const PLACEHOLDER_COMPANIES = new Set(['unknown', 'unknown company']);
const PLACEHOLDER_STATUSES = new Set(['unknown']);

function normalizeToken(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function isGarbageTitle(value: string): boolean {
  return GARBAGE_TITLES.has(normalizeToken(value).toLowerCase());
}

function isPlaceholderCompany(value: string): boolean {
  return PLACEHOLDER_COMPANIES.has(normalizeToken(value).toLowerCase());
}

function isPlaceholderStatus(value: string): boolean {
  return PLACEHOLDER_STATUSES.has(normalizeToken(value).toLowerCase());
}

function getMeaningfulCompany(item: Pick<WorkItem, 'company'>): string | null {
  const company = normalizeToken(item.company);
  return company && !isPlaceholderCompany(company) ? company : null;
}

function getMeaningfulStatus(item: Pick<WorkItem, 'status'>): string | null {
  const status = normalizeToken(item.status);
  return status && !isPlaceholderStatus(status) ? status : null;
}

function getSummaryFirstLine(summaryValue: string | null | undefined): string | null {
  const summary = normalizeToken(summaryValue);
  if (!summary) return null;
  return summary.split(/\r?\n/, 1)[0] ?? null;
}

function humanizeEnumLabel(value: string | null | undefined): string | null {
  const normalized = normalizeToken(value);
  if (!normalized) return null;

  return normalized
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getCompanyStatusLabel(item: Pick<WorkItem, 'company' | 'status'>): string | null {
  const company = getMeaningfulCompany(item);
  if (!company) return null;

  const status = getMeaningfulStatus(item);
  return status ? `${company} — ${status}` : company;
}

export function getWorkItemTypeLabel(type: string | null | undefined): string | null {
  return humanizeEnumLabel(type);
}

export function getWorkItemStateLabel(state: string | null | undefined): string | null {
  return humanizeEnumLabel(state);
}

/**
 * Resolve the primary display label for a WorkItem.
 *
 * Normative rule (single source of truth across Today, Workbench, Drawer):
 *   1. `title` when non-empty and not a garbage placeholder
 *   2. `job_title` for application-shaped rows (type APPLICATION or legacy)
 *   3. `company — status` when company is meaningful
 *   4. First line of `summary` (truncated to 60 chars)
 *   5. Explicit "Untitled Item"
 */
export function getWorkItemDisplayTitle(item: WorkItem): string {
  const title = normalizeToken(item.title);
  if (title && !isGarbageTitle(title)) return title;

  const jobTitle = normalizeToken(item.job_title);
  if (jobTitle && !isGarbageTitle(jobTitle)) return jobTitle;

  const companyStatus = getCompanyStatusLabel(item);
  if (companyStatus) {
    return companyStatus;
  }

  const firstLine = getSummaryFirstLine(item.summary);
  if (firstLine) {
    return firstLine.length > 60 ? firstLine.slice(0, 60) + '…' : firstLine;
  }

  return 'Untitled Item';
}

/**
 * Optional secondary line (e.g. company) shown below the primary title.
 * Returns null when it would duplicate the primary title or has no useful value.
 */
export function getWorkItemSecondaryLine(item: WorkItem): string | null {
  const primaryTitle = getWorkItemDisplayTitle(item);
  const company = getMeaningfulCompany(item);
  const companyStatus = getCompanyStatusLabel(item);

  if (!company) return null;
  if (primaryTitle === company) return null;
  if (companyStatus && primaryTitle === companyStatus) return null;

  return company;
}

export type WorkItemSourceKind = 'email' | 'document' | 'manual' | 'unknown';

export interface WorkItemSourceInfo {
  sourceType: WorkItemSourceKind;
  label: string;
  hasLink: boolean;
}

/**
 * Classifies work item provenance for display (Today board pills, drawer lineage).
 * Mirrors TodayPage bucketing: email → document → manual; "unknown" is used when
 * the manual bucket has no source, gmail id, or document id (honest uncertainty).
 */
function hasDocumentExtractionSnapshot(item: Pick<WorkItem, 'meta'>): boolean {
  const meta = item.meta && typeof item.meta === 'object' ? (item.meta as Record<string, unknown>) : {};
  return Boolean(meta.source_document_filename || meta.extraction_supersedes_key);
}

/** Aligns with ``merge_source_lineage`` channel keys (email / document / manual). */
function lineageCreationChannel(
  sl: WorkItemSourceLineage | null | undefined,
): WorkItemSourceKind | null {
  if (!sl || typeof sl !== 'object') return null;
  const st = String(sl.source_type || '')
    .trim()
    .toLowerCase();
  const createdFrom = String(sl.created_from || '')
    .trim()
    .toLowerCase();
  const docIdLineage = sl.document_id != null && String(sl.document_id).trim().length > 0;

  if (st === 'email') return 'email';
  if (st === 'document') return 'document';
  if (st === 'manual' || createdFrom === 'manual') return 'manual';
  if (createdFrom === 'document' || docIdLineage) return 'document';
  return null;
}

function legacyCreationChannel(
  item: Pick<WorkItem, 'source' | 'gmail_message_id' | 'document_id' | 'meta'>,
): WorkItemSourceInfo {
  const sourceNormalized = (item.source || '').toLowerCase().trim();
  const isEmailSourced = sourceNormalized.includes('email') || Boolean(item.gmail_message_id);
  const snapshotDoc = hasDocumentExtractionSnapshot(item);
  const isDocumentSourced =
    sourceNormalized === 'document' ||
    (sourceNormalized.length === 0 && !isEmailSourced && Boolean(item.document_id)) ||
    (sourceNormalized.length === 0 && !isEmailSourced && snapshotDoc);

  if (isEmailSourced) {
    return { sourceType: 'email', label: 'Gmail', hasLink: false };
  }
  if (isDocumentSourced) {
    return {
      sourceType: 'document',
      label: 'From document',
      hasLink: Boolean(item.document_id),
    };
  }

  const hasNoLineageHints =
    sourceNormalized.length === 0 && !item.gmail_message_id && !item.document_id && !snapshotDoc;
  if (hasNoLineageHints) {
    return { sourceType: 'unknown', label: 'Unknown source', hasLink: false };
  }

  return { sourceType: 'manual', label: 'Manually created', hasLink: false };
}

function channelsInConflict(a: WorkItemSourceKind, b: WorkItemSourceKind): boolean {
  if (a === b) return false;
  if (a === 'unknown' || b === 'unknown') return false;
  return true;
}

function sourceInfoForChannel(
  channel: WorkItemSourceKind,
  item: Pick<WorkItem, 'document_id' | 'meta' | 'source_lineage'>,
): WorkItemSourceInfo {
  const sl = item.source_lineage;
  const docId = Boolean(item.document_id) || Boolean(sl && sl.document_id);
  if (channel === 'email') return { sourceType: 'email', label: 'Gmail', hasLink: false };
  if (channel === 'document') {
    return { sourceType: 'document', label: 'From document', hasLink: docId };
  }
  if (channel === 'manual') return { sourceType: 'manual', label: 'Manually created', hasLink: false };
  return { sourceType: 'unknown', label: 'Unknown source', hasLink: false };
}

/**
 * Prefer durable ``source_lineage`` from ``merge_source_lineage`` when it encodes a clear
 * creation channel; fall back to legacy ``source`` / ids / extraction snapshot. When both
 * lineage and legacy encode different concrete channels, return unknown (NFR3 / Story 7.6).
 */
export function getWorkItemSourceInfo(
  item: Pick<WorkItem, 'source' | 'gmail_message_id' | 'document_id' | 'meta' | 'source_lineage'>,
): WorkItemSourceInfo {
  const fromLineage = lineageCreationChannel(item.source_lineage);
  const legacy = legacyCreationChannel(item);

  if (fromLineage == null) {
    return legacy;
  }
  if (legacy.sourceType === 'unknown') {
    return sourceInfoForChannel(fromLineage, item);
  }
  if (channelsInConflict(fromLineage, legacy.sourceType)) {
    return { sourceType: 'unknown', label: 'Unknown source', hasLink: false };
  }
  return sourceInfoForChannel(fromLineage, item);
}

const _CLASSIFICATION_SOURCE_LABELS: Record<ClassificationSource, string> = {
  heuristic: 'Heuristics',
  ai_enrichment: 'AI enrichment',
  user_override: 'Your correction',
  bulk_user_approved: 'Bulk approval',
  migration: 'Migration',
  system_default: 'System default',
  unknown: 'Unknown classifier',
};

const _KNOWN_ENRICHMENT_PATH_LABELS: Record<string, string> = {
  llm_extraction: 'LLM extraction',
  heuristic_only: 'Heuristic only',
  user_override: 'Manual correction',
};

function humanizePathToken(value: string): string {
  const raw = value.trim();
  if (!raw) return '';
  const key = raw.toLowerCase();
  if (_KNOWN_ENRICHMENT_PATH_LABELS[key]) return _KNOWN_ENRICHMENT_PATH_LABELS[key];
  return raw
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

/**
 * Message id used to fetch original email / Gmail deep links.
 * Prefer the durable Gmail column; fall back to provider-neutral lineage.
 */
export function linkedEmailMessageId(
  item: Pick<WorkItem, 'gmail_message_id' | 'source_lineage'> | null | undefined,
): string | null {
  if (!item) return null;
  const gmail = typeof item.gmail_message_id === 'string' ? item.gmail_message_id.trim() : '';
  if (gmail) return gmail;
  const sourceId = item.source_lineage?.source_id;
  if (typeof sourceId === 'string' && sourceId.trim()) return sourceId.trim();
  return null;
}

/** Primary "Created from" line (Story 7.6) — no PII expansion. */
export function getWorkItemCreatedFromLabel(
  item: Pick<WorkItem, 'source' | 'gmail_message_id' | 'document_id' | 'meta' | 'source_lineage'>,
): string {
  const info = getWorkItemSourceInfo(item);
  if (info.sourceType === 'email' && linkedEmailMessageId(item)) return 'From Gmail';
  if (info.sourceType === 'document') return 'From document';
  if (info.sourceType === 'manual') return 'Manually created';
  if (info.sourceType === 'unknown') return 'Source unknown';
  return info.label;
}

export type ClassificationInfluenceSummary = {
  lines: string[];
  total: number;
  isEmpty: boolean;
};

const INFLUENCE_DISPLAY_CAP = 3;

/** Bounded, user-safe strings for lineage drawer (allowed keys only; NFR37). */
export function formatClassificationInfluencesSummary(
  sourceLineage: WorkItem['source_lineage'] | null | undefined,
  cap: number = INFLUENCE_DISPLAY_CAP,
): ClassificationInfluenceSummary {
  const raw = sourceLineage && typeof sourceLineage === 'object' ? sourceLineage.classification_influences : undefined;
  const list = Array.isArray(raw) ? raw : [];
  const parsed: string[] = [];
  for (const entry of list) {
    if (parsed.length >= 100) break;
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const kind = typeof e.kind === 'string' ? e.kind.trim().toLowerCase() : '';
    if (kind && kind !== 'classification') continue;
    const srcRaw = typeof e.classification_source === 'string' ? e.classification_source.trim() : '';
    if (!srcRaw || !/^[a-z_]+$/.test(srcRaw)) continue;
    const srcLabel =
      srcRaw in _CLASSIFICATION_SOURCE_LABELS
        ? _CLASSIFICATION_SOURCE_LABELS[srcRaw as ClassificationSource]
        : null;
    if (!srcLabel) continue;
    const pathRaw = typeof e.enrichment_path === 'string' ? e.enrichment_path.trim() : '';
    const labelRaw = typeof e.label === 'string' ? e.label.trim() : '';
    let line = srcLabel;
    if (pathRaw && /^[a-z0-9_\-\s]+$/i.test(pathRaw) && pathRaw.length <= 120) {
      line = `${srcLabel} · ${humanizePathToken(pathRaw)}`;
    } else if (labelRaw && labelRaw.length <= 120 && /^[\w\s.,\-–—]+$/u.test(labelRaw)) {
      line = `${srcLabel} · ${labelRaw}`;
    }
    parsed.push(line);
  }

  if (!parsed.length) {
    return { lines: [], total: 0, isEmpty: true };
  }
  const total = parsed.length;
  const lines = parsed.slice(0, Math.max(0, cap));
  return { lines, total, isEmpty: false };
}

/** @alias formatClassificationInfluencesSummary */
export const summarizeClassificationInfluencesForDisplay = formatClassificationInfluencesSummary;
